import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SelectInvoiceDialog from '../components/credit-note/SelectInvoiceDialog';
import InvoiceViewDialog from '../components/InvoiceViewDialog';
import debitNoteService from '../services/debitNoteService';
import { api } from '../utils/apiService';
import { useSnackbar } from 'notistack';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TablePagination,
  Menu,
} from '@mui/material';
import { 
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  FileDownload as ExportIcon,
  NoteAlt as DebitNoteIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';

function DebitNote() {
  const [debitNotes, setDebitNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    pages: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectInvoiceDialogOpen, setSelectInvoiceDialogOpen] = useState(false);
  
  // State for the view debit note dialog
  const [viewDebitNoteDialog, setViewDebitNoteDialog] = useState(false);
  const [selectedDebitNote, setSelectedDebitNote] = useState(null);
  const [viewDebitNoteLoading, setViewDebitNoteLoading] = useState(false);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Fetch new page of data from API
    fetchDebitNotes(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Fetch data with new limit
    fetchDebitNotes(1, newRowsPerPage);
  };
  
  // Fetch debit notes from API
  const fetchDebitNotes = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const response = await debitNoteService.getDebitNotes({}, page, limit);
      
      if (response.success) {
        setDebitNotes(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch debit notes');
      }
    } catch (error) {
      console.error('Error fetching debit notes:', error);
      setError(error.message || 'An error occurred while fetching debit notes');
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for viewing debit note details
  const handleViewDebitNote = async (debitNote) => {
    console.log('handleViewDebitNote called with:', debitNote);
    setViewDebitNoteLoading(true);
    setSelectedDebitNote(null);
    setViewDebitNoteDialog(true);
    
    try {
      // Check if we received a debit note object or just an ID
      if (typeof debitNote === 'object' && debitNote !== null) {
        console.log('Debit note is an object');
        console.log('Debit note properties:', Object.keys(debitNote));
        
        // The InvoiceViewDialog expects a specific structure with master, details, and payments properties
        // Let's transform the debit note object to match the expected structure if needed
        
        // If we have the debit note object with an ID, use it to fetch complete data
        if (debitNote._id || debitNote.id || debitNote.invoiceNo) {
          console.log('Using debit note object to fetch complete data');
          
          // Create a properly structured debit note object for the dialog as a fallback
          // The InvoiceViewDialog component expects specific property names
          const formattedDebitNote = {
            master: {
              invoiceNo: debitNote.invoiceNo || debitNote.id || debitNote._id,
              invoiceDate: debitNote.invoiceDate || debitNote.date || new Date().toISOString(),
              customerName: debitNote.customerName || debitNote.customer || 'N/A',
              customerVatNumber: debitNote.vat || debitNote.vatNumber || 'N/A',
              customerPhone: debitNote.customerPhone || debitNote.mobileNo || '',
              customerAddress: debitNote.customerAddress || '',
              // These are the property names that InvoiceViewDialog is looking for
              // Convert all values to numbers and provide fallbacks
              subTotal: Number(debitNote.subtotal || 0),
              vatAmt: Number(debitNote.totalTax || 0),
              totalAmt: Number(debitNote.totalAmount || 0),
              paymentStatus: debitNote.paymentStatus || 'N/A',
              balanceAmt: Number(debitNote.balanceAmt || 0),
              paidAmt: Number(debitNote.paidAmt || 0),
              notes: debitNote.notes || '',
              // Additional debit note specific fields
              refInvoiceNo: debitNote.refInvoiceNo || '',
              refInvoiceDate: debitNote.refInvoiceDate || '',
              reason: debitNote.reason || ''
            },
            details: [],
            payments: []
          };
          
          console.log('Fallback formatted debit note:', formattedDebitNote);
          console.log('Financial values in fallback:', {
            subTotal: formattedDebitNote.master.subTotal,
            vatAmt: formattedDebitNote.master.vatAmt,
            totalAmt: formattedDebitNote.master.totalAmt
          });
          
          // Set the formatted debit note as a fallback
          setSelectedDebitNote(formattedDebitNote);
          
          // Fetch the complete debit note data from the API
          try {
            // Use the debit note number (invoiceNo) for the API call
            const invoiceNo = debitNote.invoiceNo;
            if (invoiceNo) {
              console.log('Fetching complete debit note data with invoice number:', invoiceNo);
              const response = await api.get(`/debitNotes/${invoiceNo}`);
              
              if (response.data.success && response.data.data) {
                console.log('Got complete debit note data from API:', response.data.data);
                console.log('Master data properties:', Object.keys(response.data.data.master));
                console.log('Master data values:', {
                  subtotal: response.data.data.master.subtotal,
                  totalTax: response.data.data.master.totalTax,
                  totalAmount: response.data.data.master.totalAmount
                });
                
                // Create a properly formatted object for the dialog
                const apiData = response.data.data;
                
                // The InvoiceViewDialog component expects specific property names
                // We need to explicitly map the fields from the DebitNoteMaster model
                const formattedApiData = {
                  master: {
                    ...apiData.master,
                    // Explicitly map the financial values to the expected property names
                    // These are the exact property names expected by InvoiceViewDialog
                    subTotal: Number(apiData.master.subtotal || 0),
                    vatAmt: Number(apiData.master.totalTax || 0),
                    totalAmt: Number(apiData.master.totalAmount || 0),
                    balanceAmt: Number(apiData.master.balanceAmt || 0),
                    paidAmt: Number(apiData.master.paidAmt || 0)
                  },
                  details: apiData.details || [],
                  payments: apiData.payments || []
                };
                
                console.log('Formatted API data for dialog:', formattedApiData);
                console.log('Financial values in formatted data:', {
                  subTotal: formattedApiData.master.subTotal,
                  vatAmt: formattedApiData.master.vatAmt,
                  totalAmt: formattedApiData.master.totalAmt
                });
                
                // Update with the formatted data
                setSelectedDebitNote(formattedApiData);
              } else {
                console.log('API response did not contain success or data:', response.data);
              }
            } else {
              console.warn('No invoice number available for fetching complete data');
            }
          } catch (fetchError) {
            console.warn('Could not fetch complete debit note data, using transformed data instead:', fetchError);
            // Continue with the transformed data we already set
          }
        } else {
          // If we don't have enough data to identify the debit note
          console.error('No valid debit note identifier found in the object');
          enqueueSnackbar('Error: Cannot identify the debit note', { variant: 'error' });
        }
      } else {
        // If we just have an ID, fetch the debit note
        console.log('Debit note is an ID:', debitNote);
        const response = await api.get(`/debitNotes/${debitNote}`);
        console.log('API response for ID:', response);
        
        if (response.data.success) {
          console.log('Setting selected debit note from API response:', response.data.data);
          setSelectedDebitNote(response.data.data);
        } else {
          console.error('API returned unsuccessful response:', response.data);
          enqueueSnackbar('Failed to fetch debit note details', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error fetching debit note details:', error);
      console.error('Error details:', error.response?.data || error.message);
      enqueueSnackbar('Error loading debit note details: ' + (error.response?.data?.message || error.message), { variant: 'error' });
    } finally {
      // Use setTimeout to log the final state after the state update has been applied
      setTimeout(() => {
        console.log('Final selectedDebitNote state after timeout:', selectedDebitNote);
      }, 0);
      setViewDebitNoteLoading(false);
    }
  };

  // Handler for closing the view dialog
  const handleCloseViewDialog = () => {
    setViewDebitNoteDialog(false);
  };
  
  // Load debit notes on component mount
  useEffect(() => {
    fetchDebitNotes(page + 1, rowsPerPage);
  }, []);

  // Filter debit notes based on search query
  const filteredDebitNotes = debitNotes.filter(note => {
    return searchQuery === '' || 
      note.invoiceNo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.refInvoiceNo.toLowerCase().includes(searchQuery.toLowerCase());
  });
  
  // Since we're using server-side pagination, we don't need to slice the data
  const paginatedDebitNotes = filteredDebitNotes;

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Debit Notes
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Manage your debit notes
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            size="small"
            sx={{ 
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setSelectInvoiceDialogOpen(true)}
            sx={{ 
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            New Debit Note
          </Button>
        </Box>
      </Box>

      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="All Debit Notes" />
      </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search debit notes..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: '100%',
            maxWidth: 500,
            height: 28,
            '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              height: 28,
            },
            '& .MuiInputBase-input': {
              py: 0.5,
            }
          }}
        />
        <Button 
          variant="outlined" 
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{ 
            borderRadius: '2px',
            textTransform: 'none',
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
          Filters
        </Button>
      </Box>
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: 0.5, 
          mb: 0.5, 
          border: '1px solid rgba(0, 0, 0, 0.12)', 
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '120px' }}>Debit Note #</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Date</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Customer</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Original Invoice</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Total Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>VAT Amount</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Grand Total</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '80px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2">Loading debit notes...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="error">{error}</Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedDebitNotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2">No debit notes found</Typography>
                    </TableCell>
                  </TableRow>
                ) : paginatedDebitNotes.map((note) => (
                  <TableRow
                    key={note._id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="left" sx={{ py: 0.5 }}>
                      <Typography variant="body2" color="primary" fontWeight="medium">
                        {note.invoiceNo}
                      </Typography>
                    </TableCell>
                    <TableCell align="left" sx={{ py: 0.5 }}>{new Date(note.invoiceDate).toLocaleDateString()}</TableCell>
                    <TableCell align="left" sx={{ py: 0.5 }}>{note.customerName}</TableCell>
                    <TableCell align="left" sx={{ py: 0.5 }}>{note.refInvoiceNo}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>SAR {note.subtotal.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>SAR {note.totalTax.toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ py: 0.5 }}>SAR {note.totalAmount.toFixed(2)}</TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Chip 
                        label={note.status} 
                        size="small" 
                        color={
                          note.status === 'Submitted' ? 'success' :
                          note.status === 'Pending' ? 'warning' :
                          'default'
                        } 
                        variant="outlined"
                        sx={{ 
                          fontWeight: 'medium',
                          backgroundColor: 
                            note.status === 'Submitted' ? 'rgba(46, 205, 111, 0.1)' :
                            note.status === 'Pending' ? 'rgba(255, 193, 7, 0.1)' :
                            'transparent'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton 
                          size="small" 
                          color="primary" 
                          sx={{ mx: 0.25, p: 0.5 }}
                          onClick={() => handleViewDebitNote(note)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ 
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>
      
      {/* Select Invoice Dialog */}
      <SelectInvoiceDialog
        open={selectInvoiceDialogOpen}
        onClose={() => setSelectInvoiceDialogOpen(false)}
        dialogTitle="Select Invoice for Debit Note"
        onSelectInvoice={(invoice) => {
          // Navigate to the new debit note page with the selected invoice data
          navigate(`/debit-notes/new?invoiceId=${invoice._id}`, {
            state: { selectedInvoice: invoice }
          });
        }}
      />
      
      {/* View Debit Note Dialog */}
      <InvoiceViewDialog
        open={viewDebitNoteDialog}
        onClose={handleCloseViewDialog}
        invoice={selectedDebitNote}
        loading={viewDebitNoteLoading}
        title="Debit Note Details"
        currencySymbol="SAR"
      />
    </Container>
  );
}

export default DebitNote;